import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Check } from "lucide-react";
import { COMPANY } from "@/lib/constants";

export const QuoteCTABanner = () => {
  return (
    <section
      id="quote"
      className="relative py-24 sm:py-32 overflow-hidden"
      data-testid="quote-cta-banner"
    >
      {/* Background: distinct diagonal banded gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0B1726] via-[#0F2540] to-[#0B1726]" />
      <div
        className="absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(135deg, #1E88E5 0 2px, transparent 2px 60px)",
        }}
      />
      <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-transparent via-[#E6B445] to-transparent" />
      <div className="absolute bottom-0 inset-x-0 h-[3px] bg-gradient-to-r from-transparent via-[#1E88E5] to-transparent" />

      <div className="relative max-w-5xl mx-auto px-6 sm:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-3 bg-[#E6B445] text-[#0B1726] font-heading font-black uppercase tracking-[0.25em] text-[11px] sm:text-xs px-5 py-2.5 rounded-sm shadow-[0_6px_24px_rgba(230,180,69,0.4)] mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0B1726] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#0B1726]" />
            </span>
            Free Instant Quote · No Obligation
          </div>

          <h2 className="font-heading text-4xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tight text-white leading-[0.95]">
            Ready for a price?<br />
            <span className="text-[#E6B445]">It takes 60 seconds.</span>
          </h2>

          <p className="mt-5 max-w-2xl mx-auto text-slate-300 text-base sm:text-lg leading-relaxed">
            Answer 4 quick questions. We'll text you an estimated price range
            instantly and follow up within 24 hours.
          </p>

          {/* Two-step bullet reassurance */}
          <div className="mt-7 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs sm:text-sm text-slate-300">
            {[
              "No sales calls or spam",
              "Quote is free & non-binding",
              "Same-week scheduling",
            ].map((b) => (
              <div key={b} className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#1E88E5]" />
                <span>{b}</span>
              </div>
            ))}
          </div>

          {/* Big CTA */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/quote"
              data-testid="quote-cta-banner-btn"
              className="group bg-[#1E88E5] hover:bg-[#42A5F5] text-white font-heading font-black tracking-[0.18em] uppercase text-base sm:text-lg px-10 py-6 rounded-sm flex items-center gap-3 transition-all shadow-[0_15px_50px_-12px_rgba(30,136,229,0.7)]"
            >
              Start My Free Quote
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href={COMPANY.phoneHref}
              data-testid="quote-cta-banner-call"
              className="bg-transparent border-2 border-white/20 hover:border-white text-white font-heading font-bold tracking-[0.15em] uppercase text-sm px-7 py-5 rounded-sm flex items-center gap-3 transition-all"
            >
              <Phone className="w-4 h-4" />
              Call or Text {COMPANY.phone}
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
